import { ref } from 'vue'
import { MenuService, type MenuItem } from '@/services/menuService'
import { useAuthStore } from '@/stores/auth'

// Authorize menu feed that carries Circulars / SOP Documents / News entities.
// (Authorize/Menu/GetMenuSubMenuList?Type=6)
const AUTH_MENU_TYPE = 6
// moduleId only selects the Authorize microservice base URL in MenuService.
const AUTHORIZE_MODULE_ID = 1

/**
 * Loads the role's Read-accessible menus from `Authorize/Menu/GetMenuSubMenuList?Type=6`
 * (the same feed MOPSidebar renders from) and lets callers check whether a given
 * menu is accessible. A menu only appears in this feed when the role has Read
 * access to it, so presence == "user may see this section".
 *
 * Usage:
 *   const { load, hasMenu } = useAuthorizeMenuAccess()
 *   const showCirculars = computed(() => hasMenu(n => n.includes('circular')))
 *   onMounted(load)
 */
export function useAuthorizeMenuAccess() {
  const menus = ref<MenuItem[]>([])
  const loaded = ref(false)
  const authStore = useAuthStore()

  async function load(): Promise<void> {
    try {
      const roleId = authStore.user?.roleId
      const orgId = authStore.user?.organizationId
      if (!roleId || !orgId) return
      menus.value = await MenuService.getMenuSubMenuList(AUTH_MENU_TYPE, roleId, orgId, AUTHORIZE_MODULE_ID)
    } catch (e) {
      console.error('useAuthorizeMenuAccess: failed to load menus', e)
      menus.value = []
    } finally {
      loaded.value = true
    }
  }

  // Reactive: reads menus.value so it re-evaluates inside a computed once loaded.
  function hasMenu(matcher: (name: string) => boolean): boolean {
    return menus.value.some((m) => matcher((m.SubMenuName || m.MenuName || '').toLowerCase()))
  }

  return { menus, loaded, load, hasMenu }
}
